import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { 
  BookOpen, MessageCircle, X, CheckCircle2, 
  GraduationCap as GradCapIcon, Mail, Phone, MapPin, 
  Star, Award, Zap, Users, Sparkles, Shield, 
  Target as TargetIcon, Lock, 
  Instagram, Facebook, Menu, Send, Loader2, ChevronLeft, ChevronRight,
  Twitter, Linkedin, Youtube, AlertCircle, ArrowRight, Globe, Heart, Handshake, ExternalLink,
  Clock, Calendar, Trophy, BookMarked, UserCheck, Briefcase, Languages, Play
} from 'lucide-react';

// Google GenAI Import
import { GoogleGenerativeAI } from "@google/generative-ai";

// Firebase Modular SDK
import { initializeApp, getApps, getApp } from "firebase/app";
import { getFirestore, doc, onSnapshot, collection, addDoc, Timestamp } from "firebase/firestore";

// --- Firebase Configuration ---
const firebaseConfig = {
  apiKey: import.meta.env.VITE_API_KEY,
  authDomain: import.meta.env.VITE_AUTH_DOMAIN || "sloal-86550.firebaseapp.com",
  projectId: import.meta.env.VITE_PROJECT_ID || "sloal-86550",
  storageBucket: import.meta.env.VITE_STORAGE_BUCKET || "sloal-86550.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_MESSAGING_SENDER_ID || "478894421882",
  appId: import.meta.env.VITE_APP_ID || "1:478894421882:web:d7aa86759d8f4303e208c3",
  measurementId: import.meta.env.VITE_MEASUREMENT_ID || "G-W798RR6RBY"
};

// Initialize Firebase
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const db = getFirestore(app);
const siteDataDoc = doc(db, "settings", "siteData");

// --- IP Tracking Helper ---
const getClientIP = async (): Promise<{ ip: string; country?: string; city?: string; region?: string }> => {
  try {
    const response = await fetch('https://ipapi.co/json/', { 
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    if (response.ok) {
      const data = await response.json();
      return {
        ip: data.ip || 'Unknown',
        country: data.country_name || undefined,
        city: data.city || undefined,
        region: data.region || undefined
      };
    }
  } catch (e) {
    console.log('ipapi.co failed, trying fallback...');
  }
  
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (response.ok) {
      const data = await response.json();
      return { ip: data.ip || 'Unknown' };
    }
  } catch (e) {
    console.log('ipify failed');
  }
  
  return { ip: 'Unknown' };
};

// --- Flag Components ---
const SlovenianFlag: React.FC<{ className?: string }> = ({ className = "w-5 h-4" }) => (
  <svg className={className} viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="5.33" fill="#FFFFFF"/>
    <rect y="5.33" width="24" height="5.33" fill="#0052B4"/>
    <rect y="10.67" width="24" height="5.33" fill="#D80027"/>
    <path d="M3 2.5L5.5 5.5L8 2.5V6.5H3V2.5Z" fill="#0052B4"/>
    <circle cx="5.5" cy="4" r="1" fill="#FFDA44"/>
  </svg>
);

const AlbanianFlag: React.FC<{ className?: string }> = ({ className = "w-5 h-4" }) => (
  <svg className={className} viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="16" fill="#D80027"/>
    <path d="M12 3L14 6H10L12 3Z" fill="#000000"/>
    <path d="M8 6H16V8H8V6Z" fill="#000000"/>
    <path d="M9 8H15V10H9V8Z" fill="#000000"/>
    <path d="M10 10H14V12H10V10Z" fill="#000000"/>
    <circle cx="12" cy="7" r="2" fill="#000000"/>
  </svg>
);

// --- Icon Map for dynamic rendering ---
const iconMap: Record<string, any> = { 
  BookOpen, Star, Award, Zap, Users, TargetIcon, Trophy, Clock, 
  UserCheck, MessageCircle, CheckCircle2, BookMarked, Heart, Sparkles, Calendar 
};

// --- Defaults ---
const DEFAULT_SITE_DATA = {
  navigation: [
    { id: 'n1', label: { sl: "Domov", sq: "Ballina" }, sectionId: 'home', isVisible: true },
    { id: 'n2', label: { sl: "Programi", sq: "Programet" }, sectionId: 'courses', isVisible: true },
    { id: 'n3', label: { sl: "Cenik", sq: "Çmimi" }, sectionId: 'pricing', isVisible: true },
    { id: 'n4', label: { sl: "O nas", sq: "Rreth nesh" }, sectionId: 'about', isVisible: true },
    { id: 'n6', label: { sl: "Partnerji", sq: "Partnerët" }, sectionId: 'partners', isVisible: true },
    { id: 'n7', label: { sl: "Kontakt", sq: "Kontakti" }, sectionId: 'contact', isVisible: true }
  ],
  heroSlides: [
    {
      id: "h1",
      title: { sl: "Znanje brez meja", sq: "Dija pa kufij" },
      desc: { sl: "Vstopite v svet priložnosti z vrhunskimi tečaji slovenskega jezika, prilagojenimi albansko govorečim.", sq: "Hyni në botën e mundësive me kurse të nivelit të lartë të gjuhës sllovene, të përshtatura për shqipfolësit." },
      images: ["https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200"]
    }
  ],
  courses: [
    { id: "c1", title: { sl: "Osnovna Slovenščina", sq: "Sllovenishtja Bazë" }, desc: { sl: "Popoln začetek za vsakodnevno komunikacijo.", sq: "Fillimi i përsosur për komunikimin e përditshëm." }, icon: "BookOpen", color: "from-blue-500 to-indigo-600" },
    { id: "c2", title: { sl: "Poslovni Jezik", sq: "Gjuha e Biznesit" }, desc: { sl: "Napredno znanje za vašo karierno rast.", sq: "Njohuri të avancuara për rritjen tuaj në karrierë." }, icon: "Award", color: "from-emerald-500 to-teal-600" },
    { id: "c3", title: { sl: "Priprava na Izpit", sq: "Përgatitja për Provim" }, desc: { sl: "Intenzivni treningi za pridobitev certifikata.", sq: "Trajnime intensive për marrjen e certifikatës." }, icon: "Star", color: "from-amber-500 to-orange-600" }
  ],
  pricing: [
    { id: "p1", title: { sl: "Začetni", sq: "Fillestar" }, price: "99€", priceType: "mesec", features: { sl: ["10 ur pouka", "Osnovno gradivo", "Online dostop"], sq: ["10 orë mësimi", "Materiale bazë", "Qasje online"] } },
    { id: "p2", title: { sl: "Napredni", sq: "I avancuar" }, price: "199€", priceType: "mesec", features: { sl: ["20 ur pouka", "Napredna gradiva", "Certifikat", "Mentor"], sq: ["20 orë mësimi", "Materiale të avancuara", "Certifikatë", "Mentor"] }, featured: true },
    { id: "p3", title: { sl: "Premium", sq: "Premium" }, price: "299€", priceType: "mesec", features: { sl: ["30 ur pouka", "Vsa gradiva", "Certifikat", "Osebni mentor", "Priprava na izpit"], sq: ["30 orë mësimi", "Të gjitha materialet", "Certifikatë", "Mentor personal", "Përgatitje për provim"] } }
  ],
  about: {
    title: { sl: "O nas", sq: "Rreth nesh" },
    description: { sl: "SLOAL je vodilni izobraževalni center slovenskega jezika za albansko govoreče. Z izkušnjami in predanostjo pomagamo našim študentom doseči jezikovne cilje.", sq: "SLOAL është qendra kryesore arsimore e gjuhës sllovene për shqipfolësit. Me përvojë dhe përkushtim, i ndihmojmë studentët tanë të arrijnë qëllimet gjuhësore." },
    images: [],
    results: { sl: "Več kot 500 uspešnih diplomantov", sq: "Më shumë se 500 të diplomuar me sukses" },
    innovation: { sl: "Inovativne metode poučevanja", sq: "Metoda inovative të mësimdhënies" },
    goals: { sl: "Naš cilj je vaš uspeh", sq: "Qëllimi ynë është suksesi juaj" }
  },
  teachers: [
    { id: "t1", name: "Ana Marija", role: { sl: "Glavna predavateljica", sq: "Ligjëruese kryesore" }, email: "ana@sloal.si", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=500" },
    { id: "t2", name: "Arber Krasniqi", role: { sl: "Mentor za integracijo", sq: "Mentor për integrim" }, email: "arber@sloal.si", image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=500" }
  ],
  partners: [
    { id: "partner1", name: "Partner 1", description: { sl: "Opis partnerja", sq: "Përshkrimi i partnerit" }, logo: "https://via.placeholder.com/200x100?text=Partner+1", website: "https://example.com" }
  ],
  contact: { 
    email: "info@sloal.si", 
    phone: "+386 41 000 000", 
    address: { sl: "Ljubljana, Slovenija", sq: "Lubjanë, Slloveni" },
    social: { facebook: "", instagram: "", whatsapp: "" }
  },
  socialMedia: { instagram: "", facebook: "", twitter: "", linkedin: "", youtube: "" },
  settings: { 
    aiAssistantVisible: true, 
    statsVisible: true, 
    whyChooseUsVisible: true, 
    howItWorksVisible: true, 
    ctaVisible: true,
    teamVisible: true 
  },
  stats: {
    students: { value: 500, suffix: "+", label: { sl: "Uspešnih študentov", sq: "Studentë të suksesshëm" } },
    courses: { value: 15, suffix: "+", label: { sl: "Aktivnih tečajev", sq: "Kurse aktive" } },
    years: { value: 10, suffix: "+", label: { sl: "Let izkušenj", sq: "Vite përvojë" } },
    satisfaction: { value: 98, suffix: "%", label: { sl: "Zadovoljstvo", sq: "Kënaqësi" } }
  },
  whyChooseUs: [
    { id: "w1", icon: "Trophy", color: "from-amber-500 to-orange-500", title: { sl: "Dokazani rezultati", sq: "Rezultate të provuara" }, desc: { sl: "Več kot 98% uspešnost na izpitih", sq: "Më shumë se 98% sukses në provime" } },
    { id: "w2", icon: "Users", color: "from-blue-500 to-indigo-500", title: { sl: "Majhne skupine", sq: "Grupe të vogla" }, desc: { sl: "Maksimalno 8 študentov na skupino", sq: "Maksimumi 8 studentë për grup" } },
    { id: "w3", icon: "Clock", color: "from-emerald-500 to-teal-500", title: { sl: "Prilagodljiv urnik", sq: "Orar fleksibël" }, desc: { sl: "Jutranji, popoldanski in večerni termini", sq: "Orare të mëngjesit, pasdites dhe mbrëmjes" } },
    { id: "w4", icon: "Award", color: "from-violet-500 to-purple-500", title: { sl: "Certificirani učitelji", sq: "Mësues të certifikuar" }, desc: { sl: "Izkušeni in usposobljeni predavatelji", sq: "Ligjërues me përvojë dhe të kualifikuar" } }
  ],
  howItWorks: [
    { id: "s1", step: 1, icon: "UserCheck", color: "from-blue-500 to-blue-600", title: { sl: "Prijava", sq: "Aplikimi" }, desc: { sl: "Izpolnite obrazec za prijavo", sq: "Plotësoni formularin e aplikimit" } },
    { id: "s2", step: 2, icon: "MessageCircle", color: "from-indigo-500 to-indigo-600", title: { sl: "Posvet", sq: "Konsultimi" }, desc: { sl: "Brezplačen uvodni posvet", sq: "Konsultim falas hyrës" } },
    { id: "s3", step: 3, icon: "BookOpen", color: "from-violet-500 to-violet-600", title: { sl: "Učenje", sq: "Mësimi" }, desc: { sl: "Začnite z učenjem", sq: "Filloni mësimin" } },
    { id: "s4", step: 4, icon: "Award", color: "from-emerald-500 to-emerald-600", title: { sl: "Certifikat", sq: "Certifikata" }, desc: { sl: "Pridobite certifikat", sq: "Merrni certifikatën" } }
  ],
  cta: {
    title: { sl: "Pripravljeni na novo poglavje?", sq: "Gati për kapitullin e ri?" },
    description: { sl: "Pridružite se več kot 500 uspešnim študentom, ki so z nami dosegli svoje jezikovne cilje.", sq: "Bashkohuni me më shumë se 500 studentë të suksesshëm që arritën qëllimet e tyre gjuhësore me ne." },
    buttonText: { sl: "Začnite danes", sq: "Filloni sot" }
  }
};

const translations: any = {
  sl: {
    nav_vpis: "PRIJAVA NA TEČAJ", hero_cta: "Razišči programe", 
    slogans: [
      "Znanje je moč.", "Most med kulturami.", "Slovenščina za vsakogar.", 
      "Vaša pot do uspeha.", "Učimo z srcem.", "Jezik odpira vrata.",
      "Skupaj do cilja.", "Inovativno učenje.", "Tvoja prihodnost začne tukaj.",
      "Odličnost v izobraževanju."
    ],
    reg_title: "Prijava na tečaj", reg_submit: "Pošlji prijavo", reg_success: "Prijava uspešna!",
    programs_title: "Naši Programi", pricing_title: "Izberite Paket", about_title: "Naša Zgodba", team_title: "Spoznajte Ekipo", partners_title: "Naši Partnerji", contact_title: "Stopite v Stik",
    footer_text: "Vse pravice pridržane © 2025 SLOAL.", 
    footer_links: "Povezave", footer_programs: "Programi", footer_follow: "Sledite nam",
    privacy: "Politika zasebnosti", terms: "Pogoji uporabe", cookies: "Piškotki",
    send_msg: "Pošlji sporočilo",
    name: "Ime in priimek", email: "E-pošta", phone: "Telefon", message: "Sporočilo", course: "Izberite program",
    email_or_phone: "E-pošta ali telefon",
    ai_title: "AI Jezikovni Asistent", ai_placeholder: "Vprašajte karkoli o učenju slovenščine...",
    per_month: "na mesec", per_hour: "na uro", per_group: "na skupino", choose_plan: "Izberi paket",
    sending: "Pošiljanje...", error: "Napaka pri pošiljanju",
    view_details: "Več podrobnosti", admin_login: "Admin",
    hero_badge: "🎓 Vodilni izobraževalni center za slovenščino",
    trusted_by: "Zaupajo nam",
    apply_now: "Prijava na tečaj",
    visit_website: "Obiščite spletno stran",
    why_choose_us: "Zakaj izbrati nas?",
    stats_students: "Študentov",
    stats_courses: "Tečajev",
    stats_years: "Let",
    stats_satisfaction: "Zadovoljstvo",
    how_it_works: "Kako poteka?",
    testimonials_title: "Kaj pravijo naši študenti",
    cta_title: "Pripravljeni na novo poglavje?",
    cta_desc: "Pridružite se več kot 500 uspešnim študentom, ki so z nami dosegli svoje jezikovne cilje.",
    cta_button: "Začnite danes",
    hero_subtitle: "za vašo prihodnost"
  },
  sq: {
    nav_vpis: "APLIKONI TANI", hero_cta: "Zbuloni programet", 
    slogans: [
      "Dija është fuqi.", "Urë midis kulturave.", "Sllovenishtja për çdokënd.", 
      "Rruga juaj drejt suksesit.", "Mësojmë me zemër.", "Gjuha hap dyert.",
      "Së bashku drejt qëllimit.", "Mësim inovativ.", "E ardhmja juaj fillon këtu.",
      "Përsosmëria në arsim."
    ],
    reg_title: "Aplikimi për kurs", reg_submit: "Dërgo aplikimin", reg_success: "Aplikimi u dërgua!",
    programs_title: "Programet Tona", pricing_title: "Zgjidhni Paketën", about_title: "Historia Jonë", team_title: "Njihni Ekipin", partners_title: "Partnerët Tanë", contact_title: "Na Kontaktoni",
    footer_text: "Të dhënat e rezervuara © 2025 SLOAL.", 
    footer_links: "Lidhjet", footer_programs: "Programet", footer_follow: "Na ndiqni",
    privacy: "Politika e privatësisë", terms: "Kushtet e përdorimit", cookies: "Cookies",
    send_msg: "Dërgo mesazh",
    name: "Emri dhe mbiemri", email: "Email", phone: "Telefoni", message: "Mesazhi", course: "Zgjidhni programin",
    email_or_phone: "Email ose telefoni",
    ai_title: "Asistenti AI", ai_placeholder: "Pyetni diçka për mësimin e sllovenishtes...",
    per_month: "në muaj", choose_plan: "Zgjidhni paketën",
    sending: "Duke dërguar...", error: "Gabim në dërgim",
    view_details: "Më shumë detaje", admin_login: "Admin",
    hero_badge: "🎓 Qendra kryesore arsimore për sllovenishten",
    trusted_by: "Na besojnë",
    apply_now: "Aplikoni për kurs",
    visit_website: "Vizitoni faqen",
    why_choose_us: "Pse të na zgjidhni?",
    stats_students: "Studentë",
    stats_courses: "Kurse",
    stats_years: "Vite",
    stats_satisfaction: "Kënaqësi",
    how_it_works: "Si funksionon?",
    testimonials_title: "Çfarë thonë studentët tanë",
    cta_title: "Gati për kapitullin e ri?",
    cta_desc: "Bashkohuni me më shumë se 500 studentë të suksesshëm që arritën qëllimet e tyre gjuhësore me ne.",
    cta_button: "Filloni sot",
    hero_subtitle: "për të ardhmen tuaj"
  }
};

// Premium AI Assistant Component - FIX: Uses VITE_GEMINI_API_KEY
const GeminiAssistant: React.FC<{ lang: 'sl' | 'sq' }> = ({ lang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [chat, setChat] = useState<{ role: 'user' | 'ai', text: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const t = translations[lang];

  const handleSend = async () => {
    if (!query.trim() || loading) return;
    const userMsg = query;
    setQuery('');
    setChat(prev => [...prev, { role: 'user', text: userMsg }]);
    setLoading(true);

    try {
      // FIX: Use dedicated Gemini API key instead of Firebase API key
      const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
      if (!apiKey) throw new Error('Gemini API key not configured');
      
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({ 
        model: 'gemini-1.5-flash',
        systemInstruction: `Ste prijazen in profesionalen pomočnik izobraževalnega centra SLOAL - vodilnega centra za učenje slovenskega jezika za albansko govoreče. 
        
Vaše naloge:
- Pomagate uporabnikom pri vprašanjih o učenju slovenskega jezika
- Odgovarjate na vprašanja o tečajih, cenah in programih
- Svetujete glede izbire ustreznega programa
- Pomagate pri postopku prijave

Odgovarjate v jeziku uporabnika (${lang === 'sl' ? 'slovenščina' : 'albanščina'}). 
Bodite kratki, strokovni, prijazni in spodbudni.
Vedno spodbujajte k prijavi na tečaj.`
      });
      
      const result = await model.generateContent(userMsg);
      const response = await result.response;
      const aiResponseText = response.text() || (lang === 'sl' ? "Napaka pri povezavi." : "Gabim lidhjeje.");
      
      setChat(prev => [...prev, { role: 'ai', text: aiResponseText }]);
    } catch (e) {
      console.error('AI Error:', e);
      setChat(prev => [...prev, { role: 'ai', text: lang === 'sl' ? "AI trenutno ni na voljo. Prosimo, kontaktirajte nas neposredno." : "AI momentalisht nuk është i disponueshëm. Ju lutemi na kontaktoni drejtpërdrejt." }]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [chat]);

  return (
    <div className="fixed bottom-6 right-6 z-[1000]">
      {isOpen ? (
        <div className="w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200/50 flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <Sparkles size={20} />
              </div>
              <div>
                <span className="font-bold text-sm block">{t.ai_title}</span>
                <p className="text-[10px] text-blue-200">SLOAL Asistent</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-2 rounded-lg transition-all"><X size={18}/></button>
          </div>
          
          <div ref={scrollRef} className="h-72 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {chat.length === 0 && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Languages size={28} className="text-blue-600" />
                </div>
                <p className="text-slate-700 font-semibold text-sm mb-1">{lang === 'sl' ? 'Pozdravljeni!' : 'Përshëndetje!'}</p>
                <p className="text-slate-500 text-xs">{lang === 'sl' ? 'Kako vam lahko pomagam?' : 'Si mund t\'ju ndihmoj?'}</p>
              </div>
            )}
            {chat.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-xl text-sm ${m.role === 'user' ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white' : 'bg-white border border-slate-200 shadow-sm text-slate-700'}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-center p-2"><Loader2 className="animate-spin text-blue-600" size={20} /></div>}
          </div>
          
          <div className="p-3 bg-white border-t border-slate-100 flex gap-2">
            <input 
              type="text" value={query} onChange={e => setQuery(e.target.value)} 
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder={t.ai_placeholder}
              className="flex-1 bg-slate-100 rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
            />
            <button onClick={handleSend} className="p-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all"><Send size={16}/></button>
          </div>
        </div>
      ) : (
        <button onClick={() => setIsOpen(true)} className="w-14 h-14 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:scale-105 hover:shadow-xl transition-all border-2 border-white">
          <MessageCircle size={24}/>
        </button>
      )}
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter: React.FC<{ end: number; suffix?: string; duration?: number }> = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <div ref={ref}>{count}{suffix}</div>;
};

const App: React.FC = () => {
  const [lang, setLang] = useState<'sl' | 'sq'>('sl');
  const [siteData, setSiteData] = useState<any>(DEFAULT_SITE_DATA);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegModalOpen, setIsRegModalOpen] = useState(false);
  const [isRegSubmitted, setIsRegSubmitted] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sloganIndex, setSloganIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  // Registration form state
  const [regForm, setRegForm] = useState({ name: '', emailOrPhone: '', course: '', message: '' });
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');

  // Contact form state
  const [contactForm, setContactForm] = useState({ name: '', emailOrPhone: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  const t = translations[lang];

  const handleOpenRegModal = (courseTitle?: string) => {
    if (courseTitle) {
      setRegForm(prev => ({ ...prev, course: courseTitle }));
    }
    setIsRegModalOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setSloganIndex(prev => (prev + 1) % t.slogans.length), 4000);
    return () => clearInterval(timer);
  }, [lang, t.slogans.length]);

  useEffect(() => {
    if ((siteData.heroSlides || []).length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % siteData.heroSlides.length);
        setCurrentImageIndex(0);
      }, 15000);
      return () => clearInterval(timer);
    }
  }, [siteData.heroSlides?.length]);

  const currentImages = (siteData.heroSlides || [])[currentSlide]?.images || [];
  useEffect(() => {
    if (currentImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex(prev => (prev + 1) % currentImages.length);
      }, 8000);
      return () => clearInterval(timer);
    }
  }, [currentImages.length, currentSlide]);

  useEffect(() => { setCurrentImageIndex(0); }, [currentSlide]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    const setupFirestore = async () => {
      try {
        unsubscribe = onSnapshot(
          siteDataDoc, 
          (snap) => { 
            if (snap.exists()) {
              const data = snap.data();
              if (data.contact && !data.contact.social) {
                data.contact.social = { facebook: "", instagram: "", whatsapp: "" };
              }
              const mergedData = {
                ...DEFAULT_SITE_DATA,
                ...data,
                contact: { ...DEFAULT_SITE_DATA.contact, ...data.contact },
                about: { ...DEFAULT_SITE_DATA.about, ...data.about },
                socialMedia: { ...DEFAULT_SITE_DATA.socialMedia, ...data.socialMedia },
                settings: { ...DEFAULT_SITE_DATA.settings, ...data.settings },
                partners: data.partners || DEFAULT_SITE_DATA.partners,
                stats: { ...DEFAULT_SITE_DATA.stats, ...data.stats },
                whyChooseUs: data.whyChooseUs || DEFAULT_SITE_DATA.whyChooseUs,
                howItWorks: data.howItWorks || DEFAULT_SITE_DATA.howItWorks,
                cta: { ...DEFAULT_SITE_DATA.cta, ...data.cta }
              };
              setSiteData(mergedData);
              setIsDataLoaded(true);
            } else {
              setIsDataLoaded(true);
            }
          },
          () => { setIsDataLoaded(true); }
        );
      } catch {
        setIsDataLoaded(true);
      }
    };

    setupFirestore();
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  const getTxt = (obj: any): string => obj?.[lang] || obj?.sl || "";
  
  const getFeatures = (featuresObj: any): string[] => {
    if (!featuresObj) return [];
    const result = featuresObj[lang] || featuresObj.sl || [];
    return Array.isArray(result) ? result : [];
  };
  
  const scrollToSection = (id: string) => { 
    const el = document.getElementById(id); 
    if (el) window.scrollTo({ top: el.offsetTop - 80, behavior: 'smooth' }); 
    setIsMobileMenuOpen(false);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % (siteData.heroSlides || []).length);
    setCurrentImageIndex(0);
  };
  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + (siteData.heroSlides || []).length) % (siteData.heroSlides || []).length);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (currentImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % currentImages.length);
    }
  };
  const prevImage = () => {
    if (currentImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length);
    }
  };

  const parseEmailOrPhone = (value: string) => {
    const isEmail = value.includes('@');
    return { email: isEmail ? value : '', phone: isEmail ? '' : value };
  };

  // Input sanitization helper
  const sanitizeInput = (value: string): string => {
    return value.replace(/<[^>]*>/g, '').trim();
  };

  const handleRegSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegLoading(true);
    setRegError('');

    const sanitizedName = sanitizeInput(regForm.name);
    const sanitizedEmailOrPhone = sanitizeInput(regForm.emailOrPhone);
    const sanitizedMessage = sanitizeInput(regForm.message);
    const { email, phone } = parseEmailOrPhone(sanitizedEmailOrPhone);

    // Basic validation
    if (sanitizedName.length < 2 || sanitizedName.length > 100) {
      setRegError(lang === 'sl' ? 'Ime mora imeti 2-100 znakov.' : 'Emri duhet të ketë 2-100 karaktere.');
      setRegLoading(false);
      return;
    }

    try {
      const ipInfo = await getClientIP();
      
      await addDoc(collection(db, "registrations"), {
        name: sanitizedName, 
        email: email, 
        phone: phone,
        course: sanitizeInput(regForm.course), 
        message: sanitizedMessage,
        status: 'pending', 
        notes: '', 
        createdAt: Timestamp.now(),
        ipAddress: ipInfo.ip,
        country: ipInfo.country || null,
        city: ipInfo.city || null,
        region: ipInfo.region || null,
        userAgent: navigator.userAgent,
        language: navigator.language,
        referrer: document.referrer || null
      });

      setIsRegSubmitted(true);
      setRegForm({ name: '', emailOrPhone: '', course: '', message: '' });
      
      setTimeout(() => {
        setIsRegModalOpen(false);
        setIsRegSubmitted(false);
      }, 2500);
    } catch {
      setRegError(t.error);
    } finally {
      setRegLoading(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError('');
    setContactSuccess(false);

    const sanitizedName = sanitizeInput(contactForm.name);
    const sanitizedEmailOrPhone = sanitizeInput(contactForm.emailOrPhone);
    const sanitizedMessage = sanitizeInput(contactForm.message);
    const { email, phone } = parseEmailOrPhone(sanitizedEmailOrPhone);

    // Basic validation
    if (sanitizedName.length < 2 || sanitizedName.length > 100) {
      setContactError(lang === 'sl' ? 'Ime mora imeti 2-100 znakov.' : 'Emri duhet të ketë 2-100 karaktere.');
      setContactLoading(false);
      return;
    }
    if (sanitizedMessage.length < 5) {
      setContactError(lang === 'sl' ? 'Sporočilo je prekratko.' : 'Mesazhi është shumë i shkurtër.');
      setContactLoading(false);
      return;
    }

    try {
      const ipInfo = await getClientIP();
      
      await addDoc(collection(db, "messages"), {
        name: sanitizedName, 
        email: email,
        phone: phone, 
        message: sanitizedMessage,
        status: 'new', 
        createdAt: Timestamp.now(),
        ipAddress: ipInfo.ip,
        country: ipInfo.country || null,
        city: ipInfo.city || null,
        region: ipInfo.region || null,
        userAgent: navigator.userAgent,
        language: navigator.language,
        referrer: document.referrer || null
      });

      setContactSuccess(true);
      setContactForm({ name: '', emailOrPhone: '', message: '' });
      setTimeout(() => setContactSuccess(false), 3000);
    } catch {
      setContactError(t.error);
    } finally {
      setContactLoading(false);
    }
  };

  // Settings flags from Firebase
  const isAiAssistantVisible = siteData.settings?.aiAssistantVisible ?? true;
  const isStatsVisible = siteData.settings?.statsVisible !== false;
  const isWhyChooseUsVisible = siteData.settings?.whyChooseUsVisible !== false;
  const isHowItWorksVisible = siteData.settings?.howItWorksVisible !== false;
  const isCtaVisible = siteData.settings?.ctaVisible !== false;
  const isTeamVisible = siteData.settings?.teamVisible !== false;
  const isPartnersVisible = (siteData.navigation || []).find((n: any) => n.sectionId === 'partners')?.isVisible ?? true;

  const currentSlideData = (siteData.heroSlides || [])[currentSlide] || {};

  // Get dynamic stats from Firebase
  const statsData = siteData.stats || DEFAULT_SITE_DATA.stats;
  // Get dynamic whyChooseUs from Firebase
  const whyChooseUsData = siteData.whyChooseUs || DEFAULT_SITE_DATA.whyChooseUs;
  // Get dynamic howItWorks from Firebase
  const howItWorksData = siteData.howItWorks || DEFAULT_SITE_DATA.howItWorks;
  // Get dynamic CTA from Firebase
  const ctaData = siteData.cta || DEFAULT_SITE_DATA.cta;

  if (!isDataLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse shadow-xl">
            <GradCapIcon size={40} className="text-white" />
          </div>
          <Loader2 className="animate-spin text-blue-400 mx-auto mb-4" size={32} />
          <p className="text-blue-200 font-bold text-sm uppercase tracking-widest">SLOAL</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-inter selection:bg-blue-600 selection:text-white overflow-x-hidden">
      <style>{`
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #f8fafc; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(to bottom, #3b82f6, #6366f1); border-radius: 8px; }
        ::-webkit-scrollbar-thumb:hover { background: linear-gradient(to bottom, #2563eb, #4f46e5); }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-10px); } }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.3); } 50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.5); } }
        .animate-float { animation: float 4s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
        .glass { background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(12px); }
        .glass-dark { background: rgba(15, 23, 42, 0.98); backdrop-filter: blur(12px); }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* MOBILE NAV */}
      <div className={`fixed inset-0 glass-dark z-[200] transition-all duration-300 ${isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
        <div className="p-6 h-full flex flex-col">
          <div className="flex justify-between items-center mb-10">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <GradCapIcon size={24} className="text-white"/>
              </div>
              <span className="text-2xl font-bold text-white">
                <span className="text-blue-400">SLO</span><span className="text-rose-400">AL</span>
              </span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 bg-white/10 rounded-lg text-white"><X size={20}/></button>
          </div>
          <nav className="flex flex-col gap-2">
            {(siteData.navigation || []).filter((n:any) => n.isVisible && n.sectionId !== 'team').map((n:any) => (
              <button key={n.id} onClick={() => scrollToSection(n.sectionId)} className="text-lg font-semibold text-white text-left p-3 rounded-xl hover:bg-white/10 transition-all">
                {getTxt(n.label)}
              </button>
            ))}
          </nav>
          <div className="mt-auto space-y-3">
            <div className="flex bg-white/10 p-1 rounded-lg">
              <button onClick={() => setLang('sl')} className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${lang === 'sl' ? 'bg-white text-blue-600' : 'text-white/70'}`}>
                <SlovenianFlag className="w-5 h-3.5" /><span>SL</span>
              </button>
              <button onClick={() => setLang('sq')} className={`flex-1 px-3 py-2 rounded-md text-sm font-semibold transition-all flex items-center justify-center gap-2 ${lang === 'sq' ? 'bg-white text-rose-500' : 'text-white/70'}`}>
                <AlbanianFlag className="w-5 h-3.5" /><span>SQ</span>
              </button>
            </div>
            <button onClick={() => { handleOpenRegModal(); setIsMobileMenuOpen(false); }} className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold text-sm">
              {t.nav_vpis}
            </button>
          </div>
        </div>
      </div>

      {/* HEADER */}
      <nav className={`fixed top-0 w-full z-[100] transition-all duration-300 ${scrollY > 20 ? 'glass shadow-sm border-b border-slate-100' : 'bg-transparent'}`}>
        <div className="h-16 lg:h-20 max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({top:0, behavior:'smooth'})}>
            <div className="w-10 h-10 lg:w-11 lg:h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white shadow-md">
              <GradCapIcon size={22}/>
            </div>
            <div className="flex flex-col">
              <div className="text-xl lg:text-2xl font-bold tracking-tight">
                <span className="text-blue-600">SLO</span><span className="text-rose-500">AL</span>
              </div>
              <div className="h-3 overflow-hidden relative w-28 lg:w-36">
                {t.slogans.map((slogan: string, idx: number) => (
                  <p key={idx} className={`text-[8px] lg:text-[9px] uppercase font-medium text-slate-400 absolute top-0 left-0 whitespace-nowrap transition-all duration-300 ${idx === sloganIndex ? 'opacity-100' : 'opacity-0'}`}>
                    {slogan}
                  </p>
                ))}
              </div>
            </div>
          </div>
          
          <div className="hidden lg:flex items-center gap-6">
            {(siteData.navigation || []).filter((n:any) => n.isVisible && n.sectionId !== 'team').map((n:any) => (
              <button key={n.id} onClick={() => scrollToSection(n.sectionId)} className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 hover:text-blue-600 transition-colors">
                {getTxt(n.label)}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2 lg:gap-3">
            <div className="flex bg-slate-100 p-0.5 rounded-lg">
              <button onClick={() => setLang('sl')} className={`px-2.5 py-1.5 rounded-md text-[10px] font-semibold transition-all flex items-center gap-1 ${lang === 'sl' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400'}`}>
                <SlovenianFlag className="w-4 h-3" /><span>SL</span>
              </button>
              <button onClick={() => setLang('sq')} className={`px-2.5 py-1.5 rounded-md text-[10px] font-semibold transition-all flex items-center gap-1 ${lang === 'sq' ? 'bg-white text-rose-500 shadow-sm' : 'text-slate-400'}`}>
                <AlbanianFlag className="w-4 h-3" /><span>SQ</span>
              </button>
            </div>
            <button onClick={() => handleOpenRegModal()} className="hidden sm:flex items-center gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 lg:px-5 py-2 lg:py-2.5 rounded-lg font-semibold text-[11px] uppercase shadow-md hover:shadow-lg transition-all">
              {t.nav_vpis}<ArrowRight size={12} />
            </button>
            <button onClick={() => setIsMobileMenuOpen(true)} className="lg:hidden p-2 bg-slate-900 text-white rounded-lg">
              <Menu size={18}/>
            </button>
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <main className="pt-16 lg:pt-20">
        <section id="home" className="min-h-[70vh] lg:min-h-[75vh] relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50">
          <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-400/10 rounded-full blur-3xl"></div>
          
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12 lg:py-16 relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100">
                  <span className="text-xs font-medium text-slate-600">{t.hero_badge}</span>
                </div>
                
                <div className="space-y-1">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                    {getTxt(currentSlideData.title || {})}
                  </h1>
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {t.hero_subtitle}
                  </p>
                </div>
                
                <p className="text-sm lg:text-base text-slate-600 max-w-lg leading-relaxed">
                  {getTxt(currentSlideData.desc || {})}
                </p>
                
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => handleOpenRegModal()} className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all">
                    {t.nav_vpis}<ArrowRight size={16} />
                  </button>
                  <button onClick={() => scrollToSection('courses')} className="flex items-center gap-2 bg-white text-slate-700 px-6 py-3 rounded-xl font-semibold text-sm shadow-md border border-slate-200 hover:border-blue-300 transition-all">
                    {t.hero_cta}
                  </button>
                </div>

                {/* Dynamic Stats from Firebase */}
                {isStatsVisible && (
                  <div className="flex flex-wrap gap-6 pt-4">
                    {['students', 'courses', 'years', 'satisfaction'].map((key) => {
                      const stat = statsData[key];
                      if (!stat) return null;
                      const colors = { students: 'text-blue-600', courses: 'text-indigo-600', years: 'text-violet-600', satisfaction: 'text-emerald-600' };
                      return (
                        <div key={key} className="text-center">
                          <div className={`text-xl lg:text-2xl font-bold ${colors[key as keyof typeof colors]}`}>
                            <AnimatedCounter end={stat.value || 0} suffix={stat.suffix || ''} />
                          </div>
                          <p className="text-[9px] text-slate-500 font-medium uppercase">{getTxt(stat.label) || t[`stats_${key}` as keyof typeof t]}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Hero Image */}
              <div className="relative hidden lg:block">
                <div className="relative z-10">
                  {currentImages.length > 1 ? (
                    <div className="relative">
                      <div className="aspect-[4/3] bg-white rounded-2xl overflow-hidden shadow-xl border-4 border-white cursor-pointer" onClick={nextImage}>
                        {currentImages.map((img: string, idx: number) => (
                          <img key={idx} src={img || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200'} className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0'}`} alt={`Hero ${idx + 1}`} />
                        ))}
                        <div className="absolute top-3 right-3 bg-white/90 px-2.5 py-1 rounded-full shadow text-xs font-semibold text-slate-600">
                          {currentImageIndex + 1} / {currentImages.length}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar">
                        {currentImages.map((img: string, idx: number) => (
                          <button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${idx === currentImageIndex ? 'border-blue-500 shadow-md' : 'border-white/50 opacity-60 hover:opacity-100'}`}>
                            <img src={img} className="w-full h-full object-cover" alt={`Thumb ${idx + 1}`} />
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center justify-center gap-3 mt-3">
                        <button onClick={prevImage} className="p-2 bg-white hover:bg-slate-50 rounded-full shadow border border-slate-200 transition-all"><ChevronLeft size={18} className="text-slate-600" /></button>
                        <div className="flex gap-1.5">
                          {currentImages.map((_: string, idx: number) => (
                            <button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`h-1.5 rounded-full transition-all ${idx === currentImageIndex ? 'bg-blue-600 w-5' : 'bg-slate-300 w-1.5'}`} />
                          ))}
                        </div>
                        <button onClick={nextImage} className="p-2 bg-white hover:bg-slate-50 rounded-full shadow border border-slate-200 transition-all"><ChevronRight size={18} className="text-slate-600" /></button>
                      </div>
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-white rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                      <img src={currentImages[0] || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200'} className="w-full h-full object-cover" alt="Hero" />
                    </div>
                  )}
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-blue-500/20 rounded-2xl blur-xl"></div>
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-indigo-500/20 rounded-full blur-xl"></div>
              </div>
            </div>
            
            {(siteData.heroSlides || []).length > 1 && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <button onClick={prevSlide} className="p-2 bg-white hover:bg-slate-50 rounded-full shadow border border-slate-200"><ChevronLeft size={16} className="text-slate-600" /></button>
                <div className="flex gap-1.5 bg-white/80 px-3 py-1.5 rounded-full shadow">
                  {(siteData.heroSlides || []).map((_:any, idx:number) => (
                    <button key={idx} onClick={() => setCurrentSlide(idx)} className={`h-2 rounded-full transition-all ${idx === currentSlide ? 'bg-rose-500 w-6' : 'bg-slate-300 w-2'}`} />
                  ))}
                </div>
                <button onClick={nextSlide} className="p-2 bg-white hover:bg-slate-50 rounded-full shadow border border-slate-200"><ChevronRight size={16} className="text-slate-600" /></button>
              </div>
            )}
          </div>
        </section>

        {/* WHY CHOOSE US - Dynamic from Firebase */}
        {isWhyChooseUsVisible && whyChooseUsData.length > 0 && (
          <section className="py-16 lg:py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="text-center mb-10">
                <span className="inline-block bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                  {t.why_choose_us}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                  {lang === 'sl' ? 'Vaš uspeh je naša prioriteta' : 'Suksesi juaj është prioriteti ynë'}
                </h2>
              </div>
              
              <div className={`grid sm:grid-cols-2 ${whyChooseUsData.length >= 4 ? 'lg:grid-cols-4' : whyChooseUsData.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4`}>
                {whyChooseUsData.map((item: any) => {
                  const Icon = iconMap[item.icon] || Award;
                  return (
                    <div key={item.id} className="bg-slate-50 p-5 rounded-xl hover:shadow-md transition-all group">
                      <div className={`w-12 h-12 rounded-xl mb-4 flex items-center justify-center bg-gradient-to-br ${item.color || 'from-blue-500 to-indigo-500'} text-white shadow group-hover:scale-105 transition-transform`}>
                        <Icon size={22} />
                      </div>
                      <h3 className="text-base font-bold mb-1 text-slate-900">{getTxt(item.title)}</h3>
                      <p className="text-slate-500 text-sm">{getTxt(item.desc)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* HOW IT WORKS - Dynamic from Firebase */}
        {isHowItWorksVisible && howItWorksData.length > 0 && (
          <section className="py-16 lg:py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="text-center mb-10">
                <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                  {t.how_it_works}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                  {lang === 'sl' ? 'Enostavna pot do znanja' : 'Rruga e thjeshtë drejt dijes'}
                </h2>
              </div>
              
              <div className={`grid sm:grid-cols-2 ${howItWorksData.length >= 4 ? 'lg:grid-cols-4' : howItWorksData.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-6 relative`}>
                {howItWorksData.length >= 4 && (
                  <div className="hidden lg:block absolute top-10 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500"></div>
                )}
                
                {howItWorksData.map((step: any) => {
                  const Icon = iconMap[step.icon] || CheckCircle2;
                  return (
                    <div key={step.id} className="relative text-center">
                      <div className={`w-12 h-12 bg-gradient-to-br ${step.color || 'from-blue-500 to-blue-600'} rounded-xl flex items-center justify-center mx-auto mb-4 text-white shadow relative z-10`}>
                        <Icon size={22} />
                      </div>
                      <h3 className="text-sm font-bold mb-1 text-slate-900">{getTxt(step.title)}</h3>
                      <p className="text-slate-500 text-xs">{getTxt(step.desc)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* PROGRAMS */}
        <section id="courses" className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                {lang === 'sl' ? 'Naši programi' : 'Programet tona'}
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{t.programs_title}</h2>
              <p className="text-slate-500 mt-2 text-sm max-w-xl mx-auto">
                {lang === 'sl' ? 'Izberite program, ki ustreza vašim potrebam' : 'Zgjidhni programin që i përshtatet nevojave tuaja'}
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(siteData.courses || []).map((c:any) => {
                const Icon = iconMap[c.icon] || BookOpen;
                const courseTitle = getTxt(c.title);
                return (
                  <div key={c.id} className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm hover:shadow-lg hover:border-transparent transition-all group">
                    <div className={`w-14 h-14 rounded-xl mb-4 flex items-center justify-center bg-gradient-to-br ${c.color || 'from-blue-500 to-indigo-600'} text-white shadow group-hover:scale-105 transition-transform`}>
                      <Icon size={26} />
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-slate-900">{courseTitle}</h3>
                    <p className="text-slate-500 text-sm mb-4">{getTxt(c.desc)}</p>
                    <button type="button" onClick={() => handleOpenRegModal(courseTitle)} className={`w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 bg-gradient-to-r ${c.color || 'from-blue-600 to-indigo-600'} text-white shadow hover:shadow-md`}>
                      {t.apply_now}<ArrowRight size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section id="pricing" className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-white/10 text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                {lang === 'sl' ? 'Cenik' : 'Çmimi'}
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{t.pricing_title}</h2>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {(siteData.pricing || []).map((p:any) => {
                const features = getFeatures(p.features);
                return (
                  <div key={p.id} className={`relative bg-white/5 backdrop-blur-sm p-6 rounded-xl border transition-all flex flex-col ${p.featured ? 'border-blue-500 scale-[1.02] bg-white/10' : 'border-white/10 hover:border-white/20'}`}>
                    {p.featured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-1 rounded-full text-[9px] font-bold uppercase">
                        {lang === 'sl' ? 'Priporočeno' : 'Rekomanduar'}
                      </div>
                    )}
                    <div className="mb-4">
                      <h3 className="text-lg font-bold mb-1 text-white">{getTxt(p.title)}</h3>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold text-blue-400">{p.price}</span>
                        <span className="text-xs text-slate-400">/{p.priceType === 'uro' ? t.per_hour : p.priceType === 'skupina' ? t.per_group : t.per_month}</span>
                      </div>
                    </div>
                    <ul className="space-y-2.5 mb-5 flex-grow">
                      {features.map((f:string, i:number) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300 text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button type="button" onClick={() => handleOpenRegModal()} className={`w-full py-3 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${p.featured ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow' : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'}`}>
                      {t.choose_plan}<ArrowRight size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ABOUT */}
        <section id="about" className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block bg-rose-100 text-rose-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                  {lang === 'sl' ? 'O nas' : 'Rreth nesh'}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">{getTxt(siteData.about?.title)}</h2>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  {getTxt(siteData.about?.description)}
                </p>
                
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
                    <Award size={20} className="text-emerald-600 mb-2" />
                    <h4 className="font-bold text-slate-900 text-sm mb-1">{lang === 'sl' ? 'Rezultati' : 'Rezultatet'}</h4>
                    <p className="text-[10px] text-slate-600">{getTxt(siteData.about?.results)}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                    <Sparkles size={20} className="text-blue-600 mb-2" />
                    <h4 className="font-bold text-slate-900 text-sm mb-1">{lang === 'sl' ? 'Inovativnost' : 'Inovacioni'}</h4>
                    <p className="text-[10px] text-slate-600">{getTxt(siteData.about?.innovation)}</p>
                  </div>
                  <div className="bg-rose-50 p-4 rounded-xl border border-rose-100">
                    <TargetIcon size={20} className="text-rose-600 mb-2" />
                    <h4 className="font-bold text-slate-900 text-sm mb-1">{lang === 'sl' ? 'Cilji' : 'Qëllimet'}</h4>
                    <p className="text-[10px] text-slate-600">{getTxt(siteData.about?.goals)}</p>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                {(siteData.about?.images || []).length > 0 ? (
                  <div className="aspect-video rounded-xl overflow-hidden shadow-lg border-2 border-white">
                    <img src={(siteData.about?.images || [])[0]} alt="About" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-3 text-white">
                        <GradCapIcon size={32} />
                      </div>
                      <p className="text-xl font-bold text-blue-600">SLOAL</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Team - Controlled by teamVisible setting */}
            {isTeamVisible && (siteData.teachers || []).length > 0 && (
              <div className="mt-16 pt-12 border-t border-slate-100">
                <div className="text-center mb-8">
                  <span className="inline-block bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                    {lang === 'sl' ? 'Naša ekipa' : 'Ekipi ynë'}
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{t.team_title}</h3>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-5 max-w-2xl mx-auto">
                  {(siteData.teachers || []).map((teacher:any) => (
                    <div key={teacher.id} className="bg-slate-50 p-5 rounded-xl flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-white shadow">
                        <img src={teacher.image || ''} className="w-full h-full object-cover" alt={teacher.name || ''} />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900">{teacher.name}</h4>
                        <p className="text-blue-600 text-sm font-medium">{getTxt(teacher.role)}</p>
                        {teacher.email && (
                          <a href={`mailto:${teacher.email}`} className="text-slate-400 text-xs flex items-center gap-1 mt-1 hover:text-blue-600">
                            <Mail size={10} />{teacher.email}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* PARTNERS */}
        {isPartnersVisible && (siteData.partners || []).length > 0 && (
          <section id="partners" className="py-16 lg:py-20 bg-slate-50">
            <div className="max-w-7xl mx-auto px-4 lg:px-6">
              <div className="text-center mb-10">
                <span className="inline-block bg-violet-100 text-violet-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                  {lang === 'sl' ? 'Partnerji' : 'Partnerët'}
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{t.partners_title}</h2>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {(siteData.partners || []).map((partner: any) => (
                  <div key={partner.id} className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
                    <div className="h-16 mb-4 flex items-center justify-center bg-slate-50 rounded-lg p-3">
                      <img src={partner.logo || 'https://via.placeholder.com/200x100?text=Logo'} alt={partner.name || 'Partner'} className="max-h-full max-w-full object-contain" />
                    </div>
                    <div className="text-center">
                      <h3 className="font-bold text-slate-900 mb-1">{partner.name}</h3>
                      <p className="text-slate-500 text-xs mb-3">{getTxt(partner.description)}</p>
                      {partner.website && (
                        <a href={partner.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-violet-600 text-xs font-medium">
                          {t.visit_website}<ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA - Dynamic from Firebase */}
        {isCtaVisible && (
          <section className="py-16 lg:py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
            <div className="max-w-3xl mx-auto px-4 lg:px-6 text-center">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-5">
                <GradCapIcon size={28} className="text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">{getTxt(ctaData.title)}</h2>
              <p className="text-blue-100 mb-6 text-sm max-w-lg mx-auto">{getTxt(ctaData.description)}</p>
              <button onClick={() => handleOpenRegModal()} className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transition-all">
                {getTxt(ctaData.buttonText)}<ArrowRight size={16} />
              </button>
            </div>
          </section>
        )}

        {/* CONTACT */}
        <section id="contact" className="py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            <div className="grid lg:grid-cols-2 gap-10">
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 rounded-xl shadow-lg">
                <span className="inline-block bg-white/10 text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4">
                  {lang === 'sl' ? 'Kontakt' : 'Kontakti'}
                </span>
                <h2 className="text-2xl lg:text-3xl font-bold mb-6">{t.contact_title}</h2>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center"><MapPin size={18} /></div>
                    <div><p className="text-[10px] text-slate-400">{lang === 'sl' ? 'Naslov' : 'Adresa'}</p><p className="font-medium text-sm">{getTxt(siteData.contact?.address)}</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                    <div className="w-10 h-10 bg-rose-500 rounded-lg flex items-center justify-center"><Phone size={18} /></div>
                    <div><p className="text-[10px] text-slate-400">{lang === 'sl' ? 'Telefon' : 'Telefoni'}</p><p className="font-medium text-sm">{siteData.contact?.phone}</p></div>
                  </div>
                  <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                    <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center"><Mail size={18} /></div>
                    <div><p className="text-[10px] text-slate-400">Email</p><p className="font-medium text-sm">{siteData.contact?.email}</p></div>
                  </div>
                  {siteData.contact?.social?.whatsapp && (
                    <a href={`https://wa.me/${siteData.contact.social.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center"><MessageCircle size={18} /></div>
                      <div><p className="text-[10px] text-slate-400">WhatsApp</p><p className="font-medium text-sm">{siteData.contact.social.whatsapp}</p></div>
                    </a>
                  )}
                </div>
              </div>
              
              <div>
                <span className="inline-block bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4">
                  {lang === 'sl' ? 'Pišite nam' : 'Na shkruani'}
                </span>
                <h3 className="text-xl font-bold text-slate-900 mb-5">{t.send_msg}</h3>
                
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <input type="text" placeholder={t.name} value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200" required maxLength={100} />
                  <input type="text" placeholder={t.email_or_phone} value={contactForm.emailOrPhone} onChange={(e) => setContactForm({...contactForm, emailOrPhone: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200" required maxLength={100} />
                  <textarea placeholder={t.message} value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200 h-32 resize-none" required maxLength={2000}></textarea>
                  
                  {contactError && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm"><AlertCircle size={16} />{contactError}</div>
                  )}
                  {contactSuccess && (
                    <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm"><CheckCircle2 size={16} />{t.reg_success}</div>
                  )}
                  
                  <button type="submit" disabled={contactLoading} className={`w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-sm shadow-lg flex items-center justify-center gap-2 ${contactLoading ? 'opacity-50' : 'hover:shadow-xl'} transition-all`}>
                    {contactLoading ? (<><Loader2 className="animate-spin" size={16} />{t.sending}</>) : (<>{t.send_msg}<Send size={16} /></>)}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-white py-12 px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <GradCapIcon size={20} className="text-white"/>
                </div>
                <span className="text-xl font-bold">
                  <span className="text-blue-400">SLO</span><span className="text-rose-400">AL</span>
                </span>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed">
                {lang === 'sl' ? 'Vodilni izobraževalni center slovenskega jezika.' : 'Qendra kryesore arsimore e gjuhës sllovene.'}
              </p>
            </div>

            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-4">{t.footer_links}</h4>
              <ul className="space-y-2">
                {(siteData.navigation || []).filter((n:any) => n.isVisible && n.sectionId !== 'team').slice(0, 4).map((n:any) => (
                  <li key={n.id}>
                    <button onClick={() => scrollToSection(n.sectionId)} className="text-slate-400 hover:text-white text-xs transition-colors">
                      {getTxt(n.label)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-4">{t.footer_programs}</h4>
              <ul className="space-y-2">
                {(siteData.courses || []).slice(0, 4).map((c:any) => (
                  <li key={c.id}>
                    <button onClick={() => scrollToSection('courses')} className="text-slate-400 hover:text-white text-xs transition-colors">
                      {getTxt(c.title)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-4">{t.footer_follow}</h4>
              <div className="flex gap-2 flex-wrap">
                {siteData.contact?.social?.instagram && (
                  <a href={siteData.contact.social.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center hover:bg-pink-500 transition-all">
                    <Instagram size={14} className="text-slate-400 hover:text-white" />
                  </a>
                )}
                {siteData.contact?.social?.facebook && (
                  <a href={siteData.contact.social.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center hover:bg-blue-500 transition-all">
                    <Facebook size={14} className="text-slate-400 hover:text-white" />
                  </a>
                )}
                {siteData.contact?.social?.whatsapp && (
                  <a href={`https://wa.me/${siteData.contact.social.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center hover:bg-green-500 transition-all">
                    <MessageCircle size={14} className="text-slate-400 hover:text-white" />
                  </a>
                )}
              </div>
              
              <button onClick={() => setIsLoginOpen(true)} className="mt-4 w-9 h-9 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center text-slate-400 hover:text-white transition-all" title="Admin">
                <Shield size={14} />
              </button>
            </div>
          </div>

          <div className="pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-[9px] font-medium text-slate-500 uppercase tracking-widest">{t.footer_text}</p>
            <p className="text-[9px] text-slate-600 flex items-center gap-1">
              {lang === 'sl' ? 'Izdelano z' : 'Krijuar me'} <Heart size={10} className="text-rose-500" fill="currentColor" /> {lang === 'sl' ? 'v Ljubljani' : 'në Lubjanë'}
            </p>
          </div>
        </div>
      </footer>

      {/* AI Assistant */}
      {isAiAssistantVisible && <GeminiAssistant lang={lang} />}

      {/* REGISTRATION MODAL */}
      {isRegModalOpen && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-2xl max-w-md w-full relative shadow-2xl max-h-[90vh] overflow-y-auto">
            <button onClick={() => setIsRegModalOpen(false)} className="absolute top-4 right-4 text-slate-300 hover:text-slate-600 p-1"><X size={20}/></button>
            
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-3 text-white">
                <GradCapIcon size={28} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">{t.reg_title}</h2>
            </div>
            
            <form onSubmit={handleRegSubmit} className="space-y-3">
              <input type="text" placeholder={t.name} value={regForm.name} onChange={(e) => setRegForm({...regForm, name: e.target.value})} className="w-full p-3.5 bg-slate-50 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200" required maxLength={100} />
              <input type="text" placeholder={t.email_or_phone} value={regForm.emailOrPhone} onChange={(e) => setRegForm({...regForm, emailOrPhone: e.target.value})} className="w-full p-3.5 bg-slate-50 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200" required maxLength={100} />
              <select value={regForm.course} onChange={(e) => setRegForm({...regForm, course: e.target.value})} className="w-full p-3.5 bg-slate-50 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200" required>
                <option value="">{t.course}</option>
                {(siteData.courses || []).map((c:any) => (
                  <option key={c.id} value={getTxt(c.title)}>{getTxt(c.title)}</option>
                ))}
              </select>
              <textarea placeholder={t.message} value={regForm.message} onChange={(e) => setRegForm({...regForm, message: e.target.value})} className="w-full p-3.5 bg-slate-50 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200 h-24 resize-none" maxLength={2000}></textarea>
              
              {regError && (
                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded-lg flex items-center gap-2 text-sm"><AlertCircle size={14} />{regError}</div>
              )}
              
              <button type="submit" disabled={regLoading} className={`w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold text-sm shadow-lg flex items-center justify-center gap-2 ${regLoading ? 'opacity-50' : 'hover:shadow-xl'} transition-all`}>
                {regLoading ? (<><Loader2 className="animate-spin" size={16} />{t.sending}</>) : (<>{t.reg_submit}<ArrowRight size={16} /></>)}
              </button>
            </form>
            
            {isRegSubmitted && (
              <div className="absolute inset-0 bg-white flex flex-col items-center justify-center rounded-2xl">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-4 text-white animate-pulse-glow">
                  <CheckCircle2 size={40} />
                </div>
                <h4 className="text-2xl font-bold text-slate-900">{t.reg_success}</h4>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LOGIN MODAL */}
      {isLoginOpen && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4">
          <div className="bg-white p-8 rounded-2xl max-w-sm w-full text-center relative shadow-2xl">
            <button onClick={() => setIsLoginOpen(false)} className="absolute top-4 right-4 text-slate-300 hover:text-slate-600 p-1"><X size={20}/></button>
            
            <div className="w-16 h-16 bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Lock size={28}/>
            </div>
            <h3 className="text-2xl font-bold mb-1 text-slate-900">Admin</h3>
            <p className="text-slate-500 text-sm mb-6">{lang === 'sl' ? 'Prijavite se' : 'Identifikohuni'}</p>
            
            <form onSubmit={e => { e.preventDefault(); window.location.href = '/admin.html'; }} className="space-y-3">
              <input type="text" placeholder="Uporabnik" className="w-full p-3.5 bg-slate-50 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200 text-center" autoComplete="username" />
              <input type="password" placeholder="Geslo" className="w-full p-3.5 bg-slate-50 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/30 border border-slate-200 text-center" autoComplete="current-password" />
              <button type="submit" className="w-full py-4 bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-lg font-semibold text-sm shadow-lg hover:shadow-xl transition-all">
                Vstop
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const rootEl = document.getElementById('root');
if (rootEl) { 
  createRoot(rootEl).render(<App />); 
}